<script setup lang="ts">
import {
	computed,
	ref,
	onMounted,
	onBeforeUnmount,
} from 'vue';
import { useSiteGlobal } from '@zyro-inc/site-modules/use/useSiteGlobal';
import { isAppPrerendering } from '@zyro-inc/site-modules/utils/prerenderingFlags';
import BlockAppointmentList from '@zyro-inc/site-modules/components/blocks/ecommerce/BlockAppointmentList.vue';
import { useBlockEcommerceProductList } from '@zyro-inc/site-modules/components/blocks/ecommerce/useBlockEcommerceProductList';
import { SYSTEM_LOCALE } from '@zyro-inc/site-modules/constants/siteModulesConstants';
import { useSearchElementDropdown } from '@zyro-inc/site-modules/utils/useSearchElementDropdown';
import {
	EcommerceProduct,
	GOOGLE_TAG_MANAGER_EVENT,
} from '@zyro-inc/site-modules/types';

import { useProductSearch } from '@zyro-inc/site-modules/use/useProductSearch';
import { useAddProduct } from '@zyro-inc/site-modules/use/useAddProduct';
import {
	SiteBlocks,
	SiteBlockAppointmentList,
} from '@hostinger/builder-schema-validator/schema/schemaTypes';
import { useCookieBanner } from '@zyro-inc/site-modules/utils/useCookieBanner';
import { analyticsEvent } from '@zyro-inc/site-modules/utils/analyticsEvent';
import { useProductList } from '@zyro-inc/site-modules/use/useProductList';

const props = withDefaults(defineProps<{
	blockId: string;
	data: SiteBlockAppointmentList;
	lcp: {
		type: string;
		id: string;
	};
	ecommerceTranslations: Record<string, string>;
	currentLocale: string;
	blocks: SiteBlocks;
	isCartVisible: boolean;
	isInPreviewMode: boolean;
	isMobileView: boolean;
	isDynamicProductPageEnabled: boolean;
	isWhatsAppEnabled?: boolean
}>(), {
	currentLocale: SYSTEM_LOCALE,
	blocks: () => ({}),
	isCartVisible: false,
	isInPreviewMode: false,
	isMobileView: false,
	isDynamicProductPageEnabled: false,
});

const {
	siteId,
	meta,
	hasAnalyticsIntegration,
} = useSiteGlobal();

const { toggleSearchDropdown } = useSearchElementDropdown({
	blockId: props.blockId,
});

const {
	blockStyle,
	textColorVars,
	columnCount,
	productsPerPage,
	productCategoryId,
	isButtonEnabled,
	buttonDisplay,
	buttonText,
	buttonStyle,
	buttonType,
	buttonBorderWidth,
	ribbonStyle,
	imageRatio,
	productSorting,
	productSearch,
	backgroundColor,
	isCategoryListEnabled,
	disabledFilterCategoryIds,
	imageHoverEffect,
	isFullWidth,
	isTotalProductCountShown,
	columnGap,
	rowGap,
	isButtonFullWidth,
	isListCentered,
	scrollBehaviour,
	scrollToTopStyle,
	isScrollToTopEnabled,
} = useBlockEcommerceProductList(props);

const {
	isSearchResultsLoading,
	searchTerm,
	formattedSearchResults,
	updateSearchTerm,
} = useProductSearch();

const { addProductToBag } = useAddProduct({
	blockId: props.blockId,
});

const { areCookiesAllowed } = useCookieBanner(meta, props.isInPreviewMode);

const {
	pageCount,
	isProductListShown,
	isEcommerceLoading,
	handleWindowPopState,
	totalProductCount,
	currentPage,
	legacyProductPages,
	currentCategoryId,
	sorting,
	variantsQuantity,
	categories,
	isCategoriesLoaded,
	isProductReviewEnabled,
	canAddToCart,
	products,
	handlePageChange,
	handleSortChange,
	handleCategoryClick,
} = useProductList({
	data: props.data,
	blockId: props.blockId,
	isMobileView: props.isMobileView,
	isInPreviewMode: props.isInPreviewMode,
	scrollBehaviour,
	productCategoryId,
	productsPerPage,
	isCategoryListEnabled,
	isAppointmentList: true,
});

const isClientLoaded = ref(false); // needed for astro sites to not flash loader

const isLoading = computed(() => isAppPrerendering || isEcommerceLoading.value || !isClientLoaded.value);

const handleButtonClick = async (product: EcommerceProduct) => {
	addProductToBag({
		product,
		isInPreviewMode: props.isInPreviewMode,
		isCartVisible: props.isCartVisible,
	});
};

const handleAppointmentClick = (product: EcommerceProduct) => {
	if (props.isInPreviewMode || !areCookiesAllowed.value || !hasAnalyticsIntegration.value) {
		return;
	}

	analyticsEvent(GOOGLE_TAG_MANAGER_EVENT.SELECT_ITEM, {
		currency: product?.variants?.[0]?.prices?.[0]?.currency_code,
		items: [
			{
				item_id: String(product.id),
				item_name: product?.title,
				price: (Number(product?.variants?.[0]?.prices?.[0]?.amount) || 0) / 100,
			},
		],
	});
};

onMounted(() => {
	isClientLoaded.value = true;

	window.addEventListener('popstate', handleWindowPopState);
});

onBeforeUnmount(() => {
	window.removeEventListener('popstate', handleWindowPopState);
});
</script>

<template>
	<BlockAppointmentList
		:block-id="blockId"
		:block-style="blockStyle"
		:text-color-vars="textColorVars"
		:is-product-list-shown="isProductListShown"
		:products-per-page="productsPerPage"
		:column-count="columnCount"
		:page-count="pageCount"
		:total-product-count="totalProductCount"
		:current-page="currentPage"
		:legacy-product-pages="legacyProductPages"
		:product-category-id="currentCategoryId"
		:is-button-enabled="isButtonEnabled"
		:button-display="buttonDisplay"
		:button-text="buttonText"
		:button-style="buttonStyle"
		:button-type="buttonType"
		:button-border-width="buttonBorderWidth"
		:is-loading="isLoading"
		:is-categories-loaded="isCategoriesLoaded"
		:ribbon-style="ribbonStyle"
		:products="products"
		:translations="ecommerceTranslations"
		:image-ratio="imageRatio"
		:image-hover-effect="imageHoverEffect"
		:is-eager="lcp.type === 'block-appointment-list' && lcp.id === blockId"
		:site-id="siteId"
		:variants-quantity="variantsQuantity"
		:product-sorting="productSorting"
		:product-search="productSearch"
		:search-term="searchTerm"
		:sorting="sorting"
		:background-color="backgroundColor"
		:is-category-list-enabled="isCategoryListEnabled"
		:disabled-filter-category-ids="disabledFilterCategoryIds"
		:categories="categories"
		:is-full-width="isFullWidth"
		:is-total-product-count-shown="isTotalProductCountShown"
		:column-gap="columnGap"
		:row-gap="rowGap"
		:is-category-item-link-disabled="isInPreviewMode"
		:is-button-full-width="isButtonFullWidth"
		:is-list-centered="isListCentered"
		:is-mobile-view="isMobileView"
		:scroll-behaviour="scrollBehaviour"
		:is-cart-visible="isCartVisible"
		:can-add-to-cart="canAddToCart"
		:is-dynamic-product-page-enabled="isDynamicProductPageEnabled"
		:is-whats-app-enabled="isWhatsAppEnabled"
		:scroll-to-top-style="scrollToTopStyle"
		:is-scroll-to-top-enabled="isScrollToTopEnabled"
		:is-search-results-loading="isSearchResultsLoading"
		:search-results="formattedSearchResults"
		:is-product-review-enabled="isProductReviewEnabled"
		@page-changed="handlePageChange"
		@sort-changed="handleSortChange"
		@button-click="handleButtonClick"
		@category-click="handleCategoryClick"
		@is-dropdown-open="toggleSearchDropdown"
		@update:search-term="updateSearchTerm({ newValue: $event })"
		@appointment-click="handleAppointmentClick"
	/>
</template>
